/**
 * Auth API client for django-allauth headless endpoints.
 *
 * These endpoints are not part of the DRF OpenAPI schema, so we define
 * manual types here. The profile endpoint uses the shared axiosInstance.
 */

import axios from 'axios';
import { axiosInstance } from './client';

// Dedicated axios instance for allauth headless endpoints only.
// These need custom error handling (e.g. 401 from session check is not an error),
// so they use a separate instance rather than the shared axiosInstance.
const authClient = axios.create({
  baseURL: '',
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Ensure CSRF cookie is set before making POST requests
let csrfInitialized = false;

async function ensureCsrf(): Promise<void> {
  if (csrfInitialized) return;
  // Make a GET request to set the CSRF cookie (401 is expected when not logged in)
  try {
    await authClient.get('/api/_allauth/browser/v1/auth/session');
  } catch {
    // 401 is fine - we just need the cookie to be set
  }
  csrfInitialized = true;
}

// Types for allauth headless API
export interface AuthUser {
  id: number;
  username: string;
  email: string;
}

export interface AuthSessionResponse {
  status: number;
  data: {
    user: AuthUser | null;
  };
  meta: {
    is_authenticated: boolean;
  };
}

export interface AuthErrorResponse {
  status: number;
  errors: Array<{
    code: string;
    message: string;
    param?: string;
  }>;
}

// Allauth headless API endpoints - all under /api/
const ALLAUTH_BASE = '/api/_allauth/browser/v1';

/**
 * Get current session status.
 * Returns 401 with is_authenticated=false when not logged in (this is expected).
 */
export async function getSession(): Promise<AuthSessionResponse> {
  try {
    const response = await authClient.get(`${ALLAUTH_BASE}/auth/session`);
    csrfInitialized = true;
    return response.data;
  } catch (err) {
    // 401 is expected when not authenticated - extract the response data
    const error = err as { response?: { status?: number; data?: AuthSessionResponse } };
    if (error.response?.status === 401 && error.response.data) {
      csrfInitialized = true;
      return error.response.data;
    }
    throw err;
  }
}

/**
 * Initiate OAuth login by submitting a form to the provider redirect endpoint.
 * django-allauth headless API requires POST, so we create and submit a form.
 */
async function initiateOAuthLogin(provider: string, callbackUrl?: string): Promise<void> {
  await ensureCsrf();

  // Get CSRF token from cookie
  const csrfToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];

  // Create a form to submit POST request (browser will follow the redirect)
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${ALLAUTH_BASE}/auth/provider/redirect`;

  // Add form fields
  const fields = {
    provider,
    process: 'login',
    callback_url: callbackUrl || window.location.origin + '/',
    csrfmiddlewaretoken: csrfToken || '',
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * Initiate Google OAuth login.
 */
export async function initiateGoogleLogin(callbackUrl?: string): Promise<void> {
  return initiateOAuthLogin('google', callbackUrl);
}

/**
 * Initiate Microsoft OAuth login (Outlook, Hotmail, Live, Microsoft 365).
 */
export async function initiateMicrosoftLogin(callbackUrl?: string): Promise<void> {
  return initiateOAuthLogin('microsoft', callbackUrl);
}

/**
 * Logout current session.
 */
export async function logout(): Promise<void> {
  await ensureCsrf();
  await authClient.delete(`${ALLAUTH_BASE}/auth/session`);
}

// Profile types (will be in generated API after schema regeneration)
export interface UserProfile {
  id: number;
  username: string;
  email: string;
  is_superadmin?: boolean; // Only present for superadmins
}

/**
 * Get current user profile.
 */
export async function getProfile(): Promise<UserProfile> {
  const response = await axiosInstance.get('/api/auth/profile/');
  return response.data;
}

